import React, { useCallback, useContext, useRef, useState } from 'react';

import { AnimatePresence } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { ThemeContext } from 'styled-components';

import { ReactComponent as MinusIcon } from '@pkg/components/Sections/Profile/icons/minus.svg';
import { publish, useBehaviorSubject } from '@pkg/components/Sections/Profile/pubSub';
import { glShowFace } from '@pkg/components/Sections/Profile/pubSub/constants';
import { glLiquidIDFaceHoveredSubject } from '@pkg/components/Sections/Profile/pubSub/subjects';
import { useAppDispatch, useAppSelector } from '@pkg/hooks';
import { useLiquidIdRemoveItem } from '@pkg/hooks/liquid-id';

import { type TokenV2Metadata } from '@app/client';
import { PathParams } from '@app/routes/types';
import { setLiquidIDVisibleFace } from '@app/store/slices';

import { HighlightMarker, LiquidIDThumbnail, StyledAnimatedOutline, SvgWrapper } from './styles';

interface Props {
    item?: TokenV2Metadata;
    index: number;
    face: string;
    enabled: boolean;
    onThumbnailClick?: (index: number) => void;
    userDragging?: boolean;
    canAction: boolean;
    removeFace?: (number) => void;
    liquidIDFaceSelection?: number;
}

const Thumbnail = ({ item, index, face, enabled, onThumbnailClick, liquidIDFaceSelection, removeFace }: Props) => {
    const { [PathParams.Yat]: yat } = useParams();
    const primaryYat = useAppSelector((state) => state.user?.primaryYat);
    const editingLiquidID = primaryYat == yat;
    const liquidIDExpandedPreview = useAppSelector((state) => state.yatPage.liquidIDExpandedPreview);
    const [disabled, setDisabled] = useState(false);
    const { mutate: removeLidItem } = useLiquidIdRemoveItem();

    const dispatch = useAppDispatch();
    const ref = useRef<HTMLDivElement>(null);
    const [highlight, setHighlight] = useState(false);
    const { transitions } = useContext(ThemeContext);

    const memoizedFunction = useCallback(
        (d) => {
            if (liquidIDExpandedPreview) {
                setHighlight(d === index);
            }
        },
        [index, liquidIDExpandedPreview, setHighlight],
    );

    useBehaviorSubject(glLiquidIDFaceHoveredSubject, memoizedFunction);

    const animateToFace = useCallback(
        (faceName: string) => {
            dispatch(setLiquidIDVisibleFace(index));
            publish(glShowFace, faceName);
        },
        [dispatch, index],
    );

    const handleUserAction = (index: number, item?: TokenV2Metadata) => {
        if (!item || (liquidIDExpandedPreview && item)) {
            animateToFace(face);
        }
        onThumbnailClick?.(index);
    };

    const thumbnailImage = item?.image_url || item?.animation_url;

    const handleRemoveFace = () => {
        setDisabled(true);
        removeFace?.(index);
        removeLidItem(item?.queryFormat || '');
    };

    return (
        <>
            <AnimatePresence>
                {highlight && (
                    <HighlightMarker
                        key={index}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: transitions.speeds.fast, ease: transitions.eases.cubic }}
                    />
                )}
            </AnimatePresence>
            <StyledAnimatedOutline
                primaryColor="rgba(250, 6, 255, 1)"
                secondaryColor="rgba(250, 6, 255, 1)"
                baseColor="rgba(82, 61, 241, 1)"
                outlineOffset={2}
                show={liquidIDFaceSelection === index}
                transformScale={false}
                borderStrokeWidth={5}
            />

            <LiquidIDThumbnail
                $disabled={disabled}
                ref={ref}
                $nft={thumbnailImage}
                $enabled={enabled}
                $animatedOutline={liquidIDFaceSelection === index}
                $preview={liquidIDExpandedPreview}
                $highlight={highlight}
                onClick={() => handleUserAction?.(index)}
                tabIndex={enabled ? 0 : -1}
                role="button"
            >
                {editingLiquidID && !disabled && (
                    <SvgWrapper $addIcon={!item} onClick={handleRemoveFace} tabIndex={enabled ? 0 : -1} role="button">
                        <MinusIcon />
                    </SvgWrapper>
                )}
            </LiquidIDThumbnail>
        </>
    );
};

export default Thumbnail;
